import { useEffect, useState } from "react";
import { Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const CAT = { product_page: "Product page", drivers_license: "Driver's license", credit_authorization: "Signed credit authorization" };

export function ApplicationDocsDialog({ app, onClose }) {
  const [s, setS] = useState(null);

  useEffect(() => {
    if (!app) { setS(null); return undefined; }
    api.get(`/admin/applications/${app.id}/documents`).then((r) => setS(r.data || {})).catch((e) => { toast.error(errMsg(e)); setS({}); });
    return undefined;
  }, [app]);

  const open = async (d) => {
    try {
      const r = await api.get(`/admin/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      if (!/^image\/|pdf/.test(d.content_type)) a.download = d.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const docs = Array.isArray(s?.documents) ? s.documents : [];
  const auth = s?.authorization;
  return (
    <Dialog open={!!app} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border-white/10 bg-[#0b111c]" data-testid="app-docs-dialog" data-lenis-prevent>
        <DialogTitle className="font-display text-2xl">{app?.business_name}</DialogTitle>
        <DialogDescription className="font-mono text-xs text-[var(--gold)]">{app?.reference}</DialogDescription>
        {!s ? <Loader2 className="h-5 w-5 animate-spin text-sky-400" /> : (
          <div className="space-y-6 text-sm">
            <div>
              <p className="eyebrow mb-2">Product page link</p>
              {s.product_url ? <a href={s.product_url} target="_blank" rel="noreferrer" className="break-all text-sky-300 underline" data-testid="app-docs-product-url">{s.product_url}</a> : <p className="text-slate-400">Not provided</p>}
            </div>
            <div>
              <p className="eyebrow mb-2">Files ({docs.length})</p>
              {docs.length === 0 && <p className="text-slate-400">No files uploaded yet.</p>}
              <ul className="space-y-2">
                {docs.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-3 border border-white/10 bg-[#080B11] px-4 py-2.5" data-testid={`app-doc-${d.id}`}>
                    <span className="min-w-0">
                      <span className="block font-mono text-[10px] uppercase tracking-widest text-[var(--gold)]">{CAT[d.category] || d.category}</span>
                      <span className="flex items-center gap-2"><FileText className="h-4 w-4 shrink-0 text-sky-400" /><span className="truncate">{d.filename}</span></span>
                    </span>
                    <button onClick={() => open(d)} className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs text-black hover:bg-[var(--gold)]" data-testid={`app-doc-open-${d.id}`}><Download className="h-3.5 w-3.5" /> Open</button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="eyebrow mb-2">Online credit check authorization</p>
              {auth ? (
                <div className="space-y-1 border border-emerald-400/30 bg-emerald-400/5 p-4" data-testid="app-docs-auth">
                  <p className="text-emerald-300">Signed by {auth.signer_name}{auth.signer_title ? `, ${auth.signer_title}` : ""}</p>
                  <p className="font-mono text-xs text-slate-300">{new Date(auth.signed_at).toLocaleString()} · IP {auth.ip || "unknown"}</p>
                  <p className="font-mono text-[11px] text-slate-400">Wording version: {auth.text_version}</p>
                </div>
              ) : <p className="text-slate-400">Not signed online.</p>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
